import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function AdminProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  
  const [product, setProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: "",
    price: "",
  });
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchProduct = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/admin/products/${id}`);
      setProduct(data);
    } catch (err) {
      console.error("Error fetching product:", err);
      setError(err.message || "Failed to fetch product");
    } finally {
      setLoading(false);
    }
  }, [id, api]);

  React.useEffect(() => {
    fetchProduct();
  }, [id, fetchProduct]);

  const handleEditClick = () => {
    setFormData({
      name: product?.name || "",
      price: product?.price?.toString() || "",
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setFormData({
      name: "",
      price: "",
    });
  };

  const handleSaveProduct = async () => {
    try {
      // Validate form data
      if (!formData.name || !formData.name.trim()) {
        setSnackbar({
          open: true,
          message: "Product name is required",
          severity: "error",
        });
        return;
      }
      
      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        setSnackbar({
          open: true,
          message: "Valid price is required",
          severity: "error",
        });
        return;
      }

      const updatedProduct = await api.patch(`/admin/products/${id}`, {
        name: formData.name.trim(),
        price: price,
      });
      setProduct(updatedProduct);
      
      setSnackbar({
        open: true,
        message: "Product updated successfully",
        severity: "success",
      });

      handleCloseEditDialog();
    } catch (err) {
      console.error("Error updating product:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to update product",
        severity: "error",
      });
    }
  };

  if (loading) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              flexGrow: 1,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            </Container>
          </Box>
        </Box>
      </>
    );
  }

  if (error && !product) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              flexGrow: 1,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              <Alert severity="error">{error}</Alert>
            </Container>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard/products")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Products
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {product?.name || "Product Details"}
                </Typography>
              </Breadcrumbs>
            </Box>
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton onClick={() => navigate("/dashboard/products")} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {product?.name || "Product Details"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and edit product information
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
              >
                Edit Product
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ pt: 2, pb: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
                  Product Information
                </Typography>
                {loading ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Skeleton height={20} />
                      <Skeleton height={24} sx={{ mt: 0.5 }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Skeleton height={20} />
                      <Skeleton height={24} sx={{ mt: 0.5 }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Skeleton height={20} />
                      <Skeleton height={24} sx={{ mt: 0.5 }} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Name
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {product?.name || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Price
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        ${product?.price ? parseFloat(product.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Product ID
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }} color="text.secondary" fontFamily="monospace">
                        {product?.id || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
                        {product?.created_at
                          ? new Date(product.created_at).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Updated
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
                        {product?.updated_at
                          ? new Date(product.updated_at).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Edit Product Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Product Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="dense"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                margin="dense"
                required
                inputProps={{
                  step: "0.01",
                  min: "0",
                }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSaveProduct} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
